import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faUsers, faRotate, faCheckCircle, faCircleCheck,
  faPlugCircleXmark, faLink, faMobileScreen, faUserCheck, faUserXmark,
  faSpinner, faTriangleExclamation, faXmark, faCircle, faDollarSign,
  faShareNodes, faUserGroup
} from '@fortawesome/free-solid-svg-icons';

const UserManagement = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userSessions, setUserSessions] = useState([]);
  const [locationInfo, setLocationInfo] = useState(null);
  const [sharingMode, setSharingMode] = useState('INDIVIDUAL');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [agencyId, setAgencyId] = useState(null);
  const [initializingUser, setInitializingUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);

  useEffect(() => {
    fetchUserInfo();
    fetchAgencyAndSessions();
  }, [locationId]);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/auth/profile', {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) { const data = await response.json(); setUserInfo(data); }
    } catch (err) { console.error('Error fetching user info:', err); }
  };

  const fetchAgencyAndSessions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const agencyResponse = await fetch('/agencies/current', {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (agencyResponse.ok) {
        const agencyData = await agencyResponse.json();
        const extractedAgencyId = agencyData.agency?.id || agencyData.id;
        if (!extractedAgencyId) { setError('Unable to load agency information'); return; }
        setAgencyId(extractedAgencyId);
      } else {
        setError('Failed to load agency information.');
        setTimeout(() => setError(''), 5000);
      }
      await fetchUserSessions();
    } catch (err) {
      setError('Network error loading agency.');
      setTimeout(() => setError(''), 5000);
    }
  };

  const fetchUserSessions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/whatsapp/sessions/${locationId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setUserSessions(data.userSessions || []);
        const mode = (data.sharingMode || 'SHARED').toUpperCase();
        setSharingMode(mode);
        setLocationInfo({ locationId: data.locationId, total: data.total, connected: data.connected });
        setError('');
      } else if (response.status === 403) {
        setError('Unauthorized to view this location');
      } else {
        setError('Failed to fetch user sessions');
      }
    } catch (err) {
      setError('Network error while fetching user sessions');
    } finally {
      setLoading(false);
    }
  };

  const initializeUser = async (userId) => {
    try {
      setInitializingUser(userId);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/whatsapp/init-user/${locationId}/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setSuccessMessage('WhatsApp initialization started. Redirecting to QR code...');
        setTimeout(() => { navigate(`/user-whatsapp/${locationId}/${userId}`); }, 1000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to initialize WhatsApp');
        setTimeout(() => setError(''), 5000);
        setInitializingUser(null);
      }
    } catch (err) {
      setError('Network error while initializing WhatsApp');
      setTimeout(() => setError(''), 5000);
      setInitializingUser(null);
    }
  };

  const disconnectUser = async (userId) => {
    if (!window.confirm("Are you sure you want to disconnect this user's WhatsApp?")) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/whatsapp/disconnect-user/${locationId}/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setSuccessMessage('WhatsApp disconnected successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
        setInitializingUser(null);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to disconnect WhatsApp');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while disconnecting WhatsApp');
      setTimeout(() => setError(''), 5000);
    }
  };

  const setDefaultSession = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/whatsapp/set-default/${locationId}/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setSuccessMessage('Default session updated');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to set default session');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while setting default session');
      setTimeout(() => setError(''), 5000);
    }
  };

  const viewQRCode = (userId) => { navigate(`/user-whatsapp/${locationId}/${userId}`); };

  const toggleSharingMode = async () => {
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }
    const newMode = sharingMode === 'SHARED' ? 'INDIVIDUAL' : 'SHARED';
    const confirmMessage = newMode === 'INDIVIDUAL'
      ? 'Switch to Individual mode? Each user will need their own WhatsApp connection.'
      : 'Switch to Shared mode? One WhatsApp number will be used for all users.';
    if (!window.confirm(confirmMessage)) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsappSharingMode: newMode.toLowerCase() })
      });
      const data = await response.json();
      if (response.ok) {
        setSharingMode(newMode);
        setSuccessMessage(`Sharing mode changed to ${newMode}`);
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        setError(data.message || `Failed to update sharing mode`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while updating sharing mode');
      setTimeout(() => setError(''), 5000);
    }
  };

  const refreshUsers = async () => {
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      setSuccessMessage('Refreshing users from GoHighLevel...');
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}/refresh-users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(`Users refreshed! Synced: ${data.synced}, Removed: ${data.removed}`);
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to refresh users');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while refreshing users');
      setTimeout(() => setError(''), 5000);
    }
  };

  const toggleUserActive = async (userId, currentIsActive) => {
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }
    const confirmMessage = currentIsActive
      ? 'Deactivate this user? They will no longer be charged and cannot use WhatsApp.'
      : 'Activate this user? They will be charged $10/month (first user is free per location).';
    if (!window.confirm(confirmMessage)) return;
    try {
      setTogglingUser(userId);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}/users/${userId}/toggle-active`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'User status updated successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update user status');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while updating user status');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTogglingUser(null);
    }
  };

  const activateAllUsers = async () => {
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }
    const inactiveCount = userSessions.filter(u => !u.isActive).length;
    if (inactiveCount === 0) { setError('All users are already active'); setTimeout(() => setError(''), 3000); return; }
    if (!window.confirm(`Activate all ${inactiveCount} inactive user(s)? They will be charged $10/month each.`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}/users/activate-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'All users activated successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to activate users');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while activating users');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'connected': { cls: 'bg-green-100 text-green-800', dot: 'text-green-500', text: 'Connected' },
      'disconnected': { cls: 'bg-gray-100 text-gray-600', dot: 'text-gray-400', text: 'Disconnected' },
      'pending': { cls: 'bg-yellow-100 text-yellow-800', dot: 'text-yellow-500', text: 'Pending' },
      'error': { cls: 'bg-red-100 text-red-700', dot: 'text-red-500', text: 'Error' },
    };
    const b = map[status?.toLowerCase()] || map['disconnected'];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${b.cls}`}>
        <FontAwesomeIcon icon={faCircle} className={`text-xs ${b.dot}`} />
        {b.text}
      </span>
    );
  };

  const handleLogout = () => { localStorage.removeItem('access_token'); navigate('/login'); };

  if (loading) {
    return (
      <DashboardLayout userInfo={userInfo}>
        <div className="flex justify-center items-center min-h-[60vh]">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userInfo={userInfo} onLogout={handleLogout}>
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard/subaccounts')}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-900 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Subaccounts
        </button>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500" />
              {error}
            </span>
            <button onClick={() => setError('')}><FontAwesomeIcon icon={faXmark} className="text-red-500" /></button>
          </div>
        )}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
              {successMessage}
            </span>
            <button onClick={() => setSuccessMessage('')}><FontAwesomeIcon icon={faXmark} className="text-green-500" /></button>
          </div>
        )}

        {/* Header Card */}
        <div className="glass-card rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)' }}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1e1b4b, #3730a3)' }}>
                  <FontAwesomeIcon icon={faUserGroup} className="text-white" />
                </div>
                User WhatsApp Management
              </h1>
              <p className="text-sm text-gray-500 mt-1 ml-13">Location: {locationInfo?.locationId}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={activateAllUsers}
                className="btn-primary flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faUserCheck} />
                Activate All
              </button>
              <button
                onClick={refreshUsers}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faRotate} />
                Refresh Users
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-xl p-4 text-center" style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }}>
              <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Total Users</div>
              <div className="text-3xl font-bold text-blue-900">{locationInfo?.total || 0}</div>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)' }}>
              <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Connected</div>
              <div className="text-3xl font-bold text-green-900">{locationInfo?.connected || 0}</div>
            </div>
            <div
              className="rounded-xl p-4 text-center cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #faf5ff, #ede9fe)' }}
              onClick={toggleSharingMode}
              title="Click to switch sharing mode"
            >
              <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1 flex items-center justify-center gap-1">
                <FontAwesomeIcon icon={faShareNodes} className="text-xs" />
                Sharing Mode
              </div>
              <div className="text-xl font-bold text-purple-900">{sharingMode}</div>
              <div className="text-xs text-purple-500 mt-0.5">Click to switch</div>
            </div>
          </div>

          {/* Mode info banners */}
          {sharingMode === 'SHARED' && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
              <p className="text-sm text-yellow-800"><strong>Shared Mode:</strong> One WhatsApp number is used by all users.</p>
            </div>
          )}
          {sharingMode === 'INDIVIDUAL' && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-sm text-blue-800"><strong>Individual Mode:</strong> Each user has their own WhatsApp number. Messages are routed based on conversation history.</p>
            </div>
          )}
          <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-2">
            <FontAwesomeIcon icon={faDollarSign} className="text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-800"><strong>User Billing:</strong> First user is FREE per location. Additional active users are charged $10/month each. Toggle users inactive to stop billing.</p>
          </div>
        </div>

        {/* Users Table */}
        <div className="glass-card rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)' }}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #1e1b4b, #3730a3)' }}>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-indigo-100 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-indigo-100 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-indigo-100 uppercase tracking-wider">Active / Billing</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-indigo-100 uppercase tracking-wider">WhatsApp #</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-indigo-100 uppercase tracking-wider">Status</th>
                  {sharingMode === 'SHARED' && (
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-indigo-100 uppercase tracking-wider">Default</th>
                  )}
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-indigo-100 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {userSessions.length === 0 ? (
                  <tr>
                    <td colSpan={sharingMode === 'SHARED' ? 7 : 6} className="px-6 py-12 text-center text-gray-400">
                      <FontAwesomeIcon icon={faUsers} className="text-4xl mb-3 block mx-auto text-gray-300" />
                      No users found for this location
                    </td>
                  </tr>
                ) : (
                  userSessions.map((user) => (
                    <tr key={user.userId} className="hover:bg-green-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-primary-700">{(user.name || 'U')[0].toUpperCase()}</span>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{user.role || 'user'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {togglingUser === user.userId ? (
                          <FontAwesomeIcon icon={faSpinner} className="text-primary-600 animate-spin" />
                        ) : (
                          <button
                            onClick={() => toggleUserActive(user.userId, user.isActive)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                              user.isActive ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <FontAwesomeIcon icon={user.isActive ? faUserCheck : faUserXmark} />
                            {user.isActive ? 'Active' : 'Inactive'}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">
                        {user.session?.whatsappNumber || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.session?.status || 'disconnected')}
                      </td>
                      {sharingMode === 'SHARED' && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.session?.isDefault ? (
                            <span className="inline-flex items-center gap-1 text-green-700 font-semibold text-sm">
                              <FontAwesomeIcon icon={faCircleCheck} />
                              Default
                            </span>
                          ) : (
                            <button
                              onClick={() => setDefaultSession(user.userId)}
                              className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                              disabled={!user.session || user.session.status !== 'connected'}
                            >
                              Set Default
                            </button>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2 flex-wrap">
                          {initializingUser === user.userId ? (
                            <span className="flex items-center gap-1.5 text-xs text-primary-600">
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Initializing...
                            </span>
                          ) : (
                            <>
                              {(!user.client?.connected && user.session?.status !== 'connected') && (
                                <button onClick={() => initializeUser(user.userId)} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faLink} /> Initialize
                                </button>
                              )}
                              {(user.client?.connected || user.session?.status === 'connected') && (
                                <button onClick={() => disconnectUser(user.userId)} className="btn-danger text-xs px-3 py-1.5 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faPlugCircleXmark} /> Disconnect
                                </button>
                              )}
                              {(user.client?.hasQR || user.session?.status === 'pending') && (
                                <button onClick={() => viewQRCode(user.userId)} className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1">
                                  <FontAwesomeIcon icon={faMobileScreen} /> View QR
                                </button>
                              )}
                              <button
                                onClick={() => window.open(`/whatsapp/qr-latest/${locationId}/${user.userId}`, '_blank')}
                                className="text-xs px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium flex items-center gap-1"
                              >
                                <FontAwesomeIcon icon={faMobileScreen} /> QR Link
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserManagement;
